import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { User, Session } from "@supabase/supabase-js";
import { authRepo, type AuthResult } from "@/data/repos/authRepo";
import { releaseBrowserPush } from "@/data/push/browserPushSubscription";
import type { AccountKind } from "@/domain/user/types";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  /** Undefined until the database has answered for the signed-in account; null
   *  when it records no kind at all. */
  accountKind: AccountKind | null | undefined;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signUp: (
    email: string,
    password: string,
    accessCode: string,
    termsVersion: string,
  ) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<AuthResult>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedKind, setResolvedKind] = useState<{
    userId: string;
    kind: AccountKind | null;
  } | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Recovery hash hand-off: Supabase sometimes redirects to "/" with the
    // recovery token in the URL hash; force-redirect to /auth/reset-password.
    const hash = window.location.hash;
    if (
      hash &&
      hash.includes("type=recovery") &&
      !window.location.pathname.includes("/auth/reset-password")
    ) {
      window.location.replace(`${window.location.origin}/auth/reset-password${hash}`);
      return;
    }

    // Set up auth listener FIRST.
    const {
      data: { subscription },
    } = authRepo.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);

      if (event === "PASSWORD_RECOVERY") {
        navigate("/auth/reset-password");
      }
    });

    // THEN bootstrap from any existing session.
    authRepo.getSession().then((existing) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) {
      setResolvedKind(null);
      return;
    }
    if (resolvedKind?.userId === user.id) return;

    let abandoned = false;
    authRepo.getAccountKind(user.id).then((kind) => {
      if (!abandoned) setResolvedKind({ userId: user.id, kind });
    });

    return () => {
      abandoned = true;
    };
  }, [user, resolvedKind]);

  const accountKind = user && resolvedKind?.userId === user.id ? resolvedKind.kind : undefined;
  const isAccountKindPending = user !== null && accountKind === undefined;

  const signOut = async () => {
    await releaseBrowserPush();
    await authRepo.signOut();
    // Every cached page belongs to the partner who just left.
    queryClient.clear();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        accountKind,
        loading: loading || isAccountKindPending,
        signIn: (email, password) => authRepo.signIn(email, password),
        signUp: (email, password, code, termsVersion) =>
          authRepo.signUp(email, password, code, termsVersion),
        signOut,
        resetPassword: (email) => authRepo.resetPassword(email),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
